import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import useLatestRun, { ReopenTargetContext } from '@hooks/useLatestRun'
import exagoReportService from '@api/v1/exagoReportService'
import reportJobService from '@api/v1/reportJobService'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateField } from '@components/reports/specializedFilterFields'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { fmtDisplayDate } from '@utils/displayDate'
import {
  buildGridReport,
  describeExagoFilters,
  EARLIEST_DEFAULT,
  PERCENT_KEYS,
  COLUMNS,
} from './exagoAgedArGrid'
import {
  exagoJobReportId,
  exagoQueueEndpoint,
  exagoScheduleDatesNote,
  readSavedExagoResult,
  rollsWithRun,
} from './exagoAgedArJob'

/**
 * Spec 190 (CC-54303): Kucker Marino's Exago report "Aged AR by Billing Timekeeper and
 * Client with AR Notes and Payment Details", run in Exago through CC-53794 and shown in
 * the universal grouped grid.
 *
 * Exago's "Apply Parameters" prompt for this report has five parameters. CC-53794 passes
 * only the two dates, so those are this page's filters; the other three (Is Posted, Group
 * by, Active Status) run at the report's defaults, and the page says so after every run
 * rather than leaving the reader to assume they chose them.
 */

// Exago's own prompts: "Latest possible date" and "Earliest possible date".
const FIELDS = [
  dateField({ key: 'asOf', label: 'As Of', valueKey: 'asOf' }),
  dateField({ key: 'earliest', label: 'Earliest Date', valueKey: 'earliest' }),
]

const todayIso = () => dayjs().format('YYYY-MM-DD')
const makeDefaults = () => ({ asOf: todayIso(), earliest: EARLIEST_DEFAULT })

// Module-level so useLatestRun's `run` keeps one identity (spec 131). cb-api's refusals
// are written for a reader (see exago/routes.js), so they are shown as they come.
const runErrorMessage = (err) => err?.response?.data?.error || err?.message || 'The report could not be run.'

// Publish the grid's export view to the shell (CSV / XLSX / PDF of what is on screen):
// this report has no server export of its own. The grid writes its view getter straight
// into the shell's ref, and its own effect calls the shell's onPublish({ hasGroups }),
// which is what turns the export menu on. Rendered as a child of the shell, because the
// context provider lives inside it.
const PublishedGrid = ({ report, storageKey, pageStateRef, initialPageState }) => {
  const exportCtx = useReportExportView()
  return (
    <ReportGroupedGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportCtx?.ref}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
      showZeroToggle={false}
      showInactiveToggle={false}
    />
  )
}

const RunNotes = ({ result }) => (
  <div className="mb-3 space-y-1 text-xs text-rc-muted" data-testid="exago-run-notes">
    <p>
      <span className="font-semibold text-rc-ink-2">Run in Exago</span>
      {' '}as of {fmtDisplayDate(result.parameters.asOf)}
      {result.parameters.asOfMode === 'run-date' && <> (the day it ran)</>}
      {result.source?.elapsedMs != null && <> · took {(result.source.elapsedMs / 1000).toFixed(1)} s</>}
      {' '}· {result.counts.clients} client{result.counts.clients === 1 ? '' : 's'} under {result.counts.attorneys} billing attorney{result.counts.attorneys === 1 ? '' : 's'}
    </p>
    {result.parameters.fixed?.length > 0 && (
      <p data-testid="exago-fixed-parameters">
        <span className="font-semibold text-rc-ink-2">Set to the report’s defaults (Report Center can’t change these yet):</span>
        {' '}{result.parameters.fixed.map((p) => `${p.name}: ${p.value}`).join(' · ')}
      </p>
    )}
    <p>
      <span className="font-semibold text-rc-ink-2">How the Advanced Report counts:</span>
      {' '}only positive balances are included, so unallocated payments (PPD) and credits are left out; clients with no
      Billing Attorney are not included; Date of Last Payment and Amount Paid in Last 30 Days are as of today, not the
      as-of date; Amount Paid YTD is this calendar year, by date allocated; one AR note is shown per client.
    </p>
  </div>
)

const BUCKET_LABEL = Object.fromEntries(COLUMNS.map((c) => [c.key, c.label]))

// Exago's "% of total" line. A table of its own, not a grid footer row (see
// exagoAgedArGrid.js): the export writers would read "98.74%" as a currency amount.
const PercentOfTotal = ({ percents }) => {
  const shown = PERCENT_KEYS.filter((k) => percents?.[k])
  if (!shown.length) return null
  return (
    <table className="mt-3 text-xs text-rc-ink-2" data-testid="exago-percent-of-total">
      <caption className="text-left font-semibold text-rc-ink-2 pb-1">Share of Balance Due (Exago’s figures, whole report)</caption>
      <thead>
        <tr>
          {shown.map((k) => <th key={k} scope="col" className="px-3 text-right font-medium text-rc-muted">{BUCKET_LABEL[k]}</th>)}
        </tr>
      </thead>
      <tbody>
        <tr>
          {shown.map((k) => <td key={k} className="px-3 text-right tabular-nums">{percents[k]}</td>)}
        </tr>
      </tbody>
    </table>
  )
}

const ExagoAgedArPage = ({ exagoReportId, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  const [result, setResult] = useState(null)
  // Spec 192: when set, the result on screen is a saved run reopened from the queue.
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the queue's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // Saved views: the dates plus the grid's layout and quick filters (CC-49732 shape).
  // Applying one restores the filters; the run stays a click, as it costs ~30 s of Exago.
  const view = useSavedReportView(setFilters)
  const storageKey = `report:exago:${String(exagoReportId).toLowerCase()}`
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })

  // The catalog entry, for the title and summary before the first run. Shared with the
  // Reports list's query, so opening from the list costs no extra request.
  const listQuery = useQuery({ queryKey: ['exago-reports'], queryFn: () => exagoReportService.list(), staleTime: 60_000 })
  const entry = listQuery.data?.reports?.find((r) => r.id.toLowerCase() === String(exagoReportId).toLowerCase())
  const name = entry?.name || result?.report?.name || 'Advanced Report'

  const filtersSummary = useMemo(() => describeExagoFilters(filters), [filters])
  const printSubtitle = filters.asOf ? `As of ${fmtDisplayDate(filters.asOf)}` : undefined

  const runReport = useCallback(() => {
    // A reopen reads the saved run; the filters on screen are not what it ran with.
    if (!jobRef.current) {
      if (!filters.asOf) {
        invalidateRun({ error: 'Choose an as-of date to run this report.' })
        return undefined
      }
      if (filters.earliest && filters.earliest > filters.asOf) {
        invalidateRun({ error: 'The earliest date must be on or before the as-of date.' })
        return undefined
      }
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Spec 192: the queue's "View report" → the saved run's own figures and run time.
        // `useLatestRun` (spec 168, via ReopenTargetContext below) refuses a run of
        // another report before this commits.
        const [st, body] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        const data = readSavedExagoResult(body)
        return () => { setSavedAsOf(st?.createdAt || null); setResult(data) }
      }
      const data = await exagoReportService.run(exagoReportId, filters)
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setResult(data) }
    }, {
      criteria: {
        filtersSummary: describeExagoFilters(filters, []),
        printSubtitle,
      },
    })
  }, [filters, exagoReportId, startRun, invalidateRun, printSubtitle])

  // A reopen is a read, so it runs on arrival; a fresh run stays a click (~30 s of Exago).
  // Deferred and cancellable (spec 131), so StrictMode's simulated remount reads once.
  useEffect(() => {
    if (!jobId) return undefined
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const gridReport = useMemo(() => buildGridReport(result), [result])
  // The documents' criteria line (CSV / XLSX / PDF / print) names the run's dates, the
  // parameters Exago ran at its defaults, and what the report leaves out — once a run
  // has said what they were.
  const documentFilters = useMemo(
    () => (result
      ? describeExagoFilters(result.parameters, result.parameters.fixed, { scope: true, percents: result.percentOfTotal })
      : filtersSummary),
    [result, filtersSummary]
  )

  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      // Spec 192: run in the background, queue for later, or schedule — offered once the
      // list says this user has the report. A recurring run whose as-of is today asks the
      // run route for each run's own day; everything else sends the dates on screen.
      queue={entry ? {
        reportId: exagoJobReportId(exagoReportId),
        name,
        actionKey: 'exago',
        endpoint: (f, { mode } = {}) => exagoQueueEndpoint(exagoReportId, f, {
          rolling: mode === 'schedule' && rollsWithRun(f, todayIso()),
        }),
        scheduleDatesNote: (f) => exagoScheduleDatesNote(f, todayIso()),
      } : undefined}
      actions={(
        <ReportViewsMenu
          storageKey={storageKey}
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name}
      summary={entry?.summary}
      running={running}
      error={error}
      ranCriteria={ran?.criteria ? { ...ran.criteria, filtersSummary: documentFilters } : null}
      runKey={runKey}
      filterBar={filterBar}
      filtersSummary={filtersSummary}
      printSubtitle={printSubtitle}
      savedAsOf={savedAsOf}
      // A saved run's documents state ITS parameters, which its own answer carries.
      savedRunFiltersSummary={savedAsOf != null && result ? documentFilters : undefined}
      ready={!!result}
      hasGroups
    >
      {!result && !running && !error && (
        <p className="rounded-rc-card border border-rc-line bg-white p-6 text-sm text-rc-muted" data-testid="exago-intro">
          Choose an as-of date and run the report. Report Center runs the firm’s Advanced Report in Exago, which takes
          about 30 seconds. Is Posted, Group by and Active Status run at the report’s defaults.
        </p>
      )}
      {result && (
        <>
          <RunNotes result={result} />
          {result.counts.clients === 0 ? (
            <p className="rounded-rc-card border border-rc-line bg-white p-6 text-sm text-rc-muted" data-testid="exago-empty">
              Exago found no balances due as of {fmtDisplayDate(result.parameters.asOf)}.
            </p>
          ) : (
            <>
              <PublishedGrid
                key={view.gridKey}
                report={gridReport}
                storageKey={storageKey}
                pageStateRef={view.pageStateRef}
                initialPageState={view.initialPageState}
              />
              <PercentOfTotal percents={result.percentOfTotal} />
            </>
          )}
        </>
      )}
    </SpecializedReportShell>
  )
}

// The route element. Spec 168's reopen guard: `useLatestRun` refuses a `?jobId=` run
// whose report is not this one — ReportView provides the same for the 21 catalog pages.
const ExagoAgedArReport = () => {
  const { exagoReportId } = useParams()
  const [searchParams] = useSearchParams()
  const jobId = searchParams.get('jobId') || null
  const reportId = exagoJobReportId(exagoReportId)
  const reopenTarget = useMemo(() => (jobId ? { reportId, jobId } : null), [reportId, jobId])
  return (
    <ReopenTargetContext.Provider value={reopenTarget}>
      <ExagoAgedArPage key={`${reportId}:${jobId || 'live'}`} exagoReportId={exagoReportId} jobId={jobId} />
    </ReopenTargetContext.Provider>
  )
}

export default ExagoAgedArReport
